import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import useLocationInfo from 'pared/components/LocationInfo/hooks/useLocationInfo'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { IApiDataType } from '../../types'

interface IPurchaseSummaryType {
  getCustomizedReport: {
    nodes: {
      reportResult: {
        tableData: {
          difference: number
          item: string
          cogsPercentPercentileRank: number
        }[]
      }
    }[]
  }
}

const query = gql`
  query LocationPurchaseSummary($iInputParams: JSON!) {
    getCustomizedReport(
      iCustomizedReportName: "LOCATION_PURCHASE_TABLE_V2"
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

const useBbbLocationPurchaseAiSummary = (): IApiDataType => {
  const { groupFilter } = useGroupFilter()
  const dateFilter = useDateFilter()
  const startDate = dateFilter.startDate
  const endDate = dateFilter.endDate
  const locationId = groupFilter?.ids?.[0] || 0
  const locationName = useLocationInfo(locationId)?.name || 'Unknown'
  const { data, loading } = useQuery<IPurchaseSummaryType>(query, {
    variables: {
      iInputParams: {
        locationId,
        startDate,
        endDate,
      },
    },
    skip: !startDate || !endDate || !groupFilter,
  })

  return {
    data: useMemo(() => {
      const tableData =
        data?.getCustomizedReport.nodes[0]?.reportResult.tableData

      if (!tableData) return null

      const summaryMessage = (() => {
        // FIXME: BE map F&P% to cogsPercent
        const percentileRank = tableData[0]?.cogsPercentPercentileRank

        if (percentileRank <= 33) {
          return `${locationName} is underperforming and has higher F&P% than most of your group.`
        } else if (percentileRank <= 66)
          return `${locationName} is around average F&P% in your group.`

        return `Good Job!  ${locationName} has one of the best F&P% in your group.`
      })()
      const topItems = [...tableData]
        .sort((a, b) => b.difference - a.difference)
        .slice(0, 3)

      const improvementMessage = (() => {
        if (topItems.length === 0)
          return `${locationName} is purchasing items as expected for its sales. Keep up the good work!`

        return `${topItems
          .map(({ item }, index) =>
            index === topItems.length - 1 && index > 0 ? `and ${item}` : item,
          )
          .join(', ')}
          are being purchased in higher quantities than restaurants with similar sales.
          <br />
          These items represent a $${Math.round(
            topItems.reduce((sum, { difference }) => sum + difference, 0),
          )} opportunity.`
      })()

      return {
        conclusion: summaryMessage,
        insight: improvementMessage,
      }
    }, [data]),
    loading,
  }
}

export default useBbbLocationPurchaseAiSummary
